/*
 * Infinite FAQ
 * ---------------------------------------------------------------------------
 * The listed questions are the short ones. Anything typed into the ask box is
 * answered from the same content and joins the list, so the section covers far
 * more than it shows.
 *
 * Everything runs in the browser. The answers come from the blocks merchants
 * write in the theme editor (and, optionally, from the Collapsible tabs
 * sections already on the page), so there is no model call, no API key sitting
 * in a theme asset, and no request leaving the shopper's browser. When nothing
 * matches well enough it says so and points at a human instead of guessing.
 */
(() => {
  if (customElements.get('infinite-faq')) return;

  // Words that carry no routing signal, dropped before scoring.
  const STOP = new Set(('a an the is are was were be been being am do does did doing done have has had ' +
    'i me my mine we us our you your yours it its this that these those of to in on for with at by from as ' +
    'if then than so but or and not no can could should would will shall may might must about into over ' +
    'there here any some all just get anything please tell ' +
    'know need want much many very really too also still even ever there\'s im ive').split(' '));

  // Question words and filler nouns are kept rather than dropped, at a
  // fraction of the weight. Dropped, "where do you ship from" collapses to
  // "ship" and cannot be told apart from any other shipping answer. Weighted
  // fully, "what's the weather" matches every entry whose question starts with
  // "what", and "do you guys offer sponsorships" is dragged below the line by
  // two words that mean nothing. Low weight breaks ties without ever carrying
  // a match alone.
  const WEAK = new Set(('what when where which who whom how why ' +
    'something stuff thing guys offer offers').split(' '));
  const WEAK_WEIGHT = 0.25;

  // Commerce vocabulary, folded so shoppers' words meet the merchant's. Kept
  // deliberately tight: folding two words that mean different things here
  // (a return address and a shop's address) costs more than it saves, and the
  // per-question keywords in the theme editor are the place for the rest.
  const SYNONYMS = {
    refund: 'return', refunds: 'return', refunded: 'return', return: 'return', returns: 'return',
    returning: 'return', returned: 'return',
    exchange: 'exchange', exchanges: 'exchange', exchanging: 'exchange', swap: 'exchange',
    replace: 'exchange', replacement: 'exchange',
    ship: 'ship', ships: 'ship', shipped: 'ship', shipping: 'ship', shipment: 'ship', deliver: 'ship',
    delivered: 'ship', delivery: 'ship', dispatch: 'ship', postage: 'ship',
    track: 'track', tracking: 'track', tracked: 'track', arrive: 'track', arrives: 'track',
    arrived: 'track', eta: 'track',
    cancel: 'cancel', cancelled: 'cancel', canceled: 'cancel', cancellation: 'cancel',
    size: 'size', sizes: 'size', sizing: 'size', fit: 'size', fits: 'size', measurement: 'size',
    measurements: 'size',
    pay: 'pay', payment: 'pay', payments: 'pay', paypal: 'pay',
    international: 'international', internationally: 'international', overseas: 'international',
    abroad: 'international', worldwide: 'international', country: 'international',
    countries: 'international', canada: 'international', uk: 'international', europe: 'international',
    australia: 'international',
    contact: 'contact', email: 'contact', emails: 'contact', reach: 'contact', support: 'contact',
    human: 'contact', person: 'contact', someone: 'contact', help: 'contact',
    sale: 'sale', clearance: 'sale', discounted: 'sale', markdown: 'sale',
    damaged: 'damaged', damage: 'damaged', broken: 'damaged', defect: 'damaged',
    defective: 'damaged', faulty: 'damaged', ripped: 'damaged', torn: 'damaged',
    wrong: 'wrong', incorrect: 'wrong', mistake: 'wrong',
    sponsor: 'sponsor', sponsors: 'sponsor', sponsored: 'sponsor', sponsorship: 'sponsor',
    sponsorships: 'sponsor',
    worn: 'worn', wore: 'worn', wearing: 'worn', used: 'worn',
    restock: 'restock', restocked: 'restock', resupply: 'restock',
    unsubscribe: 'unsubscribe', newsletter: 'unsubscribe', marketing: 'unsubscribe',
    sms: 'unsubscribe', texts: 'unsubscribe',
    order: 'order', orders: 'order', purchase: 'order', purchased: 'order', bought: 'order',
    cost: 'cost', costs: 'cost', price: 'cost', pricing: 'cost', fee: 'cost', fees: 'cost',
    // Product words, so "can I return a hoodie" reaches answers written about items.
    shirt: 'item', shirts: 'item', tee: 'item', tees: 'item', hoodie: 'item', hoodies: 'item',
    hat: 'item', hats: 'item', cap: 'item', beanie: 'item', shorts: 'item', pants: 'item',
    joggers: 'item', jacket: 'item', crewneck: 'item', sweatshirt: 'item', product: 'item',
    products: 'item', items: 'item', item: 'item', piece: 'item',
  };

  function canon(token) {
    if (Object.prototype.hasOwnProperty.call(SYNONYMS, token)) return SYNONYMS[token];
    if (token.length > 3 && token.endsWith('ies')) return token.slice(0, -3) + 'y';
    if (token.length > 3 && token.endsWith('s') && !token.endsWith('ss')) return token.slice(0, -1);
    return token;
  }

  function normalise(text) {
    return String(text || '')
      .toLowerCase()
      .replace(/[‘’]/g, "'")
      .replace(/[^a-z0-9'\s-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function tokenise(text) {
    return normalise(text).split(' ').filter(Boolean).filter((t) => !STOP.has(t)).map(canon);
  }

  function bigrams(tokens) {
    const out = [];
    for (let i = 0; i < tokens.length - 1; i++) out.push(tokens[i] + ' ' + tokens[i + 1]);
    return out;
  }

  // How much a matched word is worth. A word in one answer identifies it; a
  // word in every answer ("order", on a store's FAQ) identifies nothing.
  function inverseFrequency(docCount, total) {
    return Math.log(1 + total / Math.max(docCount, 0.5));
  }

  // A word the FAQ has never seen — "shirt", "something", a shopper's name for
  // a thing — should dilute confidence, not destroy it. Weighting unknowns
  // like rare words would make every plainly worded question fail on the
  // padding around its one real keyword. Genuinely off-topic questions still
  // fall through, because they match nothing at all.
  const UNKNOWN_WEIGHT = 0.8;

  // Where a word was found. Keywords are the merchant's explicit routing, the
  // question is what the entry is about, and the answer body is the weakest —
  // an answer can mention shipping in passing without being the shipping
  // answer. Agreement across fields is rewarded per extra field, so the entry
  // whose keywords, question and answer all speak to a word beats one that
  // merely lists it — without that, ties fall to whichever was written first.
  const FIELD_BOOST = { keywords: 1.0, question: 0.85, answer: 0.5 };
  const MULTI_FIELD_BONUS = 0.15;

  // Two numbers per entry, deliberately:
  //   score      ranks entries against each other, and counts the answer body.
  //   confidence decides whether we are willing to assert, and counts only the
  //              keywords and the question — what the entry is *about*.
  // A word appearing in an answer in passing ("3 business days", "store
  // credit") is enough to pick the best of several answers, and not enough to
  // claim that answer is the one asked for.
  const CONFIDENT = 0.7;
  const CLOSEST = 0.4;
  const RELATED_RATIO = 0.7;
  const MAX_RELATED = 2;

  class InfiniteFAQ extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('[data-faq-list]');
      this.form = this.querySelector('[data-faq-form]');
      this.input = this.querySelector('[data-faq-input]');
      if (!this.list || !this.form || !this.input) return;

      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.template = this.querySelector('[data-faq-answer-template]');
      this.closestTemplate = this.querySelector('[data-faq-closest-template]');
      this.buildIndex();

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        const question = this.input.value.trim();
        if (!question) return;
        this.input.value = '';
        this.answer(question);
      });

      // A suggested question behaves exactly like asking it.
      this.list.addEventListener('click', (e) => {
        const chip = e.target.closest('[data-faq-suggest]');
        if (!chip) return;
        this.answer(chip.textContent.trim(), { fromSuggestion: true });
      });
    }

    // The corpus is whatever is on the page: this section's own entries, plus
    // (optionally) every Collapsible tabs question elsewhere on the page, so
    // existing FAQ content is searchable without being duplicated here.
    buildIndex() {
      const entries = [];

      this.querySelectorAll('[data-faq-entry]').forEach((el) => {
        entries.push({
          question: (el.dataset.question || '').trim(),
          keywords: el.dataset.keywords || '',
          answerHTML: el.querySelector('[data-faq-answer]') ? el.querySelector('[data-faq-answer]').innerHTML : '',
          answerText: el.querySelector('[data-faq-answer]') ? el.querySelector('[data-faq-answer]').textContent : '',
        });
      });

      if (this.dataset.harvest === 'true') {
        document.querySelectorAll('.collapsible-tabs__tab details').forEach((details) => {
          if (this.contains(details)) return;
          const summary = details.querySelector('summary');
          const panel = details.querySelector('.disclosure__content');
          if (!summary || !panel) return;
          entries.push({
            question: summary.textContent.trim(),
            keywords: '',
            answerHTML: panel.innerHTML,
            answerText: panel.textContent,
          });
        });
      }

      const docFrequency = Object.create(null);
      this.entries = entries.map((entry) => {
        const fields = {
          keywords: new Set(tokenise(entry.keywords)),
          question: new Set(tokenise(entry.question)),
          answer: new Set(tokenise(entry.answerText)),
        };
        const all = new Set([...fields.keywords, ...fields.question, ...fields.answer]);
        all.forEach((t) => { docFrequency[t] = (docFrequency[t] || 0) + 1; });
        const phrases = new Set([
          ...bigrams(tokenise(entry.question)),
          ...bigrams(tokenise(entry.keywords)),
        ]);
        return Object.assign({}, entry, { fields, phrases });
      });

      this.docFrequency = docFrequency;
      this.total = this.entries.length || 1;
    }

    // Returns the share of the question — weighted by how informative each
    // word is — that an entry actually accounts for. An unknown word counts
    // against every entry, which is what keeps off-topic questions out.
    rank(query) {
      const tokens = [...new Set(tokenise(query))];
      if (!tokens.length) return [];
      const phrases = bigrams(tokenise(query));

      const weights = tokens.map((t) => {
        if (WEAK.has(t)) return WEAK_WEIGHT;
        return this.docFrequency[t] ? inverseFrequency(this.docFrequency[t], this.total) : UNKNOWN_WEIGHT;
      });
      const totalWeight = weights.reduce((a, b) => a + b, 0) || 1;

      return this.entries
        .map((entry) => {
          let matched = 0;
          let topical = 0;
          tokens.forEach((token, i) => {
            let boost = 0;
            let topicBoost = 0;
            let fields = 0;
            let topicFields = 0;
            if (entry.fields.keywords.has(token)) {
              boost = FIELD_BOOST.keywords;
              topicBoost = FIELD_BOOST.keywords;
              fields++;
              topicFields++;
            }
            if (entry.fields.question.has(token)) {
              boost = Math.max(boost, FIELD_BOOST.question);
              topicBoost = Math.max(topicBoost, FIELD_BOOST.question);
              fields++;
              topicFields++;
            }
            if (entry.fields.answer.has(token)) {
              boost = Math.max(boost, FIELD_BOOST.answer);
              fields++;
            }
            if (fields > 1) boost += MULTI_FIELD_BONUS * (fields - 1);
            // Confidence takes its bonus only from keywords and question
            // agreeing. Letting the answer body earn it lets a word mentioned
            // in passing push an entry over the line to assert.
            if (topicFields > 1) topicBoost += MULTI_FIELD_BONUS;
            if (boost) matched += weights[i] * boost;
            if (topicBoost) topical += weights[i] * topicBoost;
          });
          let score = matched / totalWeight;
          let confidence = topical / totalWeight;
          phrases.forEach((p) => {
            if (entry.phrases.has(p)) { score += 0.12; confidence += 0.12; }
          });
          return { entry, score, confidence };
        })
        .filter((r) => r.score > 0)
        .sort((a, b) => b.score - a.score);
    }

    answer(question, options) {
      const ranked = this.rank(question);
      const best = ranked[0];
      const conf = best ? best.confidence : 0;
      // answered: say it plainly. closest: offer it as the nearest thing we
      // have and still hand over the email. none: hand over the email only.
      const outcome = conf >= CONFIDENT ? 'answered' : (conf >= CLOSEST ? 'closest' : 'none');
      const related = outcome === 'none'
        ? ranked.slice(0, MAX_RELATED).filter((r) => r.score > 0.2)
        : ranked.slice(1).filter((r) => r.score >= best.score * RELATED_RATIO).slice(0, MAX_RELATED);

      const row = document.createElement('div');
      row.className = 'infinite-faq__asked'
        + (outcome === 'none' ? ' infinite-faq__asked--unmatched' : '')
        + (outcome === 'closest' ? ' infinite-faq__asked--closest' : '');

      const heading = document.createElement('p');
      heading.className = 'infinite-faq__asked-q';
      heading.textContent = question;

      const body = document.createElement('div');
      body.className = 'infinite-faq__asked-a rte';
      body.setAttribute('aria-live', 'polite');

      if (outcome === 'none') {
        body.innerHTML = this.noMatchHTML();
      } else {
        if (outcome === 'closest') {
          const note = document.createElement('p');
          note.className = 'infinite-faq__closest-label';
          note.textContent = 'Closest thing we have written down:';
          body.appendChild(note);
        }
        const answer = document.createElement('div');
        answer.innerHTML = best.entry.answerHTML;
        while (answer.firstChild) body.appendChild(answer.firstChild);
        // A near miss gets its own line. Repeating the no-answer text here
        // reads as a contradiction: an answer, followed by "we have not
        // written that one down".
        const note = this.closestNoteHTML();
        if (outcome === 'closest' && note) {
          const fallback = document.createElement('div');
          fallback.className = 'infinite-faq__closest-fallback';
          fallback.innerHTML = note;
          body.appendChild(fallback);
        }
      }

      row.appendChild(heading);
      row.appendChild(body);

      if (related.length) {
        const also = document.createElement('div');
        also.className = 'infinite-faq__suggestions';
        const label = document.createElement('span');
        label.className = 'infinite-faq__suggestions-label';
        label.textContent = outcome === 'none' ? 'Did you mean' : 'Related';
        also.appendChild(label);
        related.forEach((r) => {
          const chip = document.createElement('button');
          chip.type = 'button';
          chip.className = 'infinite-faq__suggest';
          chip.setAttribute('data-faq-suggest', '');
          chip.textContent = r.entry.question;
          also.appendChild(chip);
        });
        row.appendChild(also);
      }

      this.list.appendChild(row);
      this.reveal(body);
      if (!options || !options.fromSuggestion) {
        row.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'center' });
      }
    }

    noMatchHTML() {
      return this.template ? this.template.innerHTML : '<p>We have not written that one down yet.</p>';
    }

    closestNoteHTML() {
      return this.closestTemplate ? this.closestTemplate.innerHTML.trim() : '';
    }

    // The answer arrives a few words at a time — a reply being given rather
    // than a block appearing. Skipped entirely for reduced-motion users.
    reveal(container) {
      if (this.reduceMotion) return;
      const nodes = [...container.querySelectorAll('p, li')];
      if (!nodes.length) return;
      const words = nodes.map((n) => n.textContent.split(' '));
      nodes.forEach((n) => {
        n.style.minHeight = `${n.getBoundingClientRect().height}px`;
        n.textContent = '';
      });

      let node = 0;
      let word = 0;
      const step = () => {
        if (node >= nodes.length) return;
        nodes[node].textContent = words[node].slice(0, ++word).join(' ');
        if (word >= words[node].length) {
          nodes[node].style.minHeight = '';
          node++;
          word = 0;
        }
        setTimeout(step, 20);
      };
      setTimeout(step, 80);
    }
  }

  customElements.define('infinite-faq', InfiniteFAQ);
})();
